import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Paperclip, Plus, Trash2 } from "lucide-react";

import { ApiError, formatDate, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Button, Cargando, ConfirmDialog, ErrorCarga, TableWrap } from "../../components/ui";
import {
  TIPO_INTERRUPCION_LABELS,
  descargarCsvInterrupciones,
  eliminarInterrupcion,
  fetchInterrupciones,
  type NivelAfectacion,
  type TipoInterrupcionAgua,
} from "./api";
import { ProduccionDiariaSection } from "./ProduccionDiariaSection";
import { RespaldoInterrupcionModal } from "./RespaldoInterrupcionModal";
import { CrearInterrupcionModal } from "./CrearInterrupcionModal";

const TIPOS: TipoInterrupcionAgua[] = [
  "Programada",
  "FuerzaMayor",
  "CasoFortuito",
];

export function InterrupcionesPanel(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<TipoInterrupcionAgua | "">("");
  const [nivel, setNivel] = useState<NivelAfectacion | "">("");
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");
  const [crearAbierto, setCrearAbierto] = useState(false);
  const [confirmarId, setConfirmarId] = useState<number | null>(null);
  const [respaldo, setRespaldo] = useState<{ id: number; numero: number } | null>(null);

  const filtros = {
    tipo: tipo || null,
    nivel: nivel || null,
    desde: desde || null,
    hasta: hasta || null,
    limit: 100,
  };

  const listQ = useQuery({
    queryKey: ["interrupciones", filtros],
    queryFn: () => fetchInterrupciones(filtros),
  });

  const exportMut = useMutation({
    mutationFn: () => descargarCsvInterrupciones(filtros),
    onSuccess: () => toast.success("CSV descargado"),
    onError: () => toast.error("No se pudo exportar"),
  });

  const eliminarMut = useMutation({
    mutationFn: (id: number) => eliminarInterrupcion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interrupciones"] });
      toast.success("Interrupción eliminada");
      setConfirmarId(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error al eliminar";
      toast.error(msg);
    },
  });

  const items = listQ.data?.items ?? [];

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-neutral-800">
          Interrupciones
        </h2>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={Download}
            onClick={() => exportMut.mutate()}
            disabled={exportMut.isPending}
          >
            CSV (SISS)
          </Button>
          <Button icon={Plus} onClick={() => setCrearAbierto(true)}>
            Nueva
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Tipo">
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoInterrupcionAgua | "")}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
          >
            <option value="">Todos</option>
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {TIPO_INTERRUPCION_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Nivel">
          <select
            value={nivel}
            onChange={(e) => setNivel(e.target.value as NivelAfectacion | "")}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
          >
            <option value="">Todos</option>
            <option value="Total">Total</option>
            <option value="Parcial">Parcial</option>
          </select>
        </Field>
        <Field label="Desde">
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
          />
        </Field>
        <Field label="Hasta">
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
          />
        </Field>
      </div>

      {listQ.isPending && <Cargando />}
      {listQ.isError && (
        <ErrorCarga
          error={listQ.error}
          mensaje="Error al cargar interrupciones."
          onReintentar={() => listQ.refetch()}
        />
      )}

      {items.length === 0 && listQ.data && (
        <p className="text-sm text-neutral-500">Sin interrupciones registradas.</p>
      )}

      {items.length > 0 && (
        <TableWrap label="Interrupciones">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-right">N°</th>
                <th scope="col" className="px-3 py-2 text-left">Inicio</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Nivel</th>
                <th scope="col" className="px-3 py-2 text-left">Motivo</th>
                <th scope="col" className="px-3 py-2 text-right">Usuarios</th>
                <th scope="col" className="px-3 py-2 text-left">Aviso usuarios</th>
                <th scope="col" className="px-3 py-2 text-left">Aviso SISS</th>
                <th scope="col" className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-500">
                    {it.numero_registro}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatDateTime(it.fecha_inicio)}
                  </td>
                  <td className="px-3 py-2">
                    {TIPO_INTERRUPCION_LABELS[it.tipo]}
                  </td>
                  <td className="px-3 py-2">{it.nivel_afectacion}</td>
                  <td className="px-3 py-2 max-w-md truncate" title={it.motivo}>
                    {it.motivo}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {it.usuarios_afectados}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {it.fecha_aviso_usuarios
                      ? formatDate(it.fecha_aviso_usuarios)
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {it.fecha_aviso_siss
                      ? formatDate(it.fecha_aviso_siss)
                      : "—"}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">
                    <Button
                      variant="link"
                      size="sm"
                      icon={Paperclip}
                      onClick={() => setRespaldo({ id: it.id, numero: it.numero_registro })}
                      title="Respaldo: aviso a usuarios, aviso a la SISS, fotos"
                    >
                      {it.evidencias?.length ? `Respaldo (${it.evidencias.length})` : "Respaldo"}
                    </Button>
                    <Button
                      variant="linkDanger"
                      size="sm"
                      icon={Trash2}
                      iconOnly
                      aria-label="Eliminar interrupción"
                      onClick={() => setConfirmarId(it.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {respaldo && (
        <RespaldoInterrupcionModal
          interrupcionId={respaldo.id}
          numero={respaldo.numero}
          onClose={() => setRespaldo(null)}
        />
      )}

      <ProduccionDiariaSection />

      {crearAbierto && (
        <CrearInterrupcionModal
          onClose={() => setCrearAbierto(false)}
          onCreated={() => {
            setCrearAbierto(false);
            qc.invalidateQueries({ queryKey: ["interrupciones"] });
            toast.success("Interrupción registrada");
          }}
        />
      )}

      <ConfirmDialog
        open={confirmarId !== null}
        title="Eliminar interrupción"
        description="Esta acción elimina el registro de interrupción. El evento queda en la bitácora de auditoría."
        confirmLabel="Eliminar"
        loading={eliminarMut.isPending}
        onConfirm={() => {
          if (confirmarId !== null) eliminarMut.mutate(confirmarId);
        }}
        onCancel={() => setConfirmarId(null)}
      />
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
